import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { EmailSubscriptionService } from '@promo/feature';
import {
  AutofillUserDataDirective,
  SdvorButtonComponent,
  SdvorCheckboxComponent,
  SdvorIconComponent,
  SdvorInputComponent,
} from '@sdvor/shared/ui/v2';
import { ExtendedCustomFormValidators } from '@shared/helpers';
import { IconTypes } from '@shared/v2';
import { User, UserProfileFacade } from '@spartacus/user';

type FormState = 'idle' | 'loading' | 'success';

@Component({
  selector: 'sd-email-subscription-modal',
  imports: [
    ReactiveFormsModule,
    RouterModule,
    AutofillUserDataDirective,
    SdvorButtonComponent,
    SdvorCheckboxComponent,
    SdvorIconComponent,
    SdvorInputComponent,
  ],
  templateUrl: './email-subscription-modal.component.html',
  styleUrls: ['./email-subscription-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailSubscriptionModalComponent {
  private fb = inject(FormBuilder);
  private emailSubscriptionService = inject(EmailSubscriptionService);
  private userProfile = inject(UserProfileFacade);
  private cdr = inject(ChangeDetectorRef);
  private user = toSignal(this.userProfile.get(), { initialValue: undefined as User | undefined });
  private loading = toSignal(this.emailSubscriptionService.loading$, { initialValue: false });
  private success = toSignal(this.emailSubscriptionService.success$, { initialValue: false });

  iconTypes = IconTypes;
  formState = computed<FormState>(() => {
    if (this.success()) {
      return 'success';
    }
    if (this.loading()) {
      return 'loading';
    }

    return 'idle';
  });

  form = this.fb.group({
    email: ['', [Validators.required, ExtendedCustomFormValidators.emailValidator]],
    consent: [false, Validators.requiredTrue],
  });

  constructor() {
    this.emailSubscriptionService.reset();
  }

  get emailControl(): AbstractControl | null {
    return this.form.get('email');
  }

  get emailError(): string | null {
    if (!this.emailControl?.touched || this.emailControl?.valid) {
      return null;
    }
    if (this.emailControl?.hasError('required')) {
      return 'Введите email';
    }
    if (this.emailControl?.hasError('email') || this.emailControl?.hasError('cxInvalidEmail')) {
      return 'Некорректный формат email';
    }

    return null;
  }

  get isSubmitDisabled(): boolean {
    return this.formState() === 'loading';
  }

  submit(): void {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      this.cdr.markForCheck();

      return;
    }

    const email = this.emailControl?.value ?? '';
    const isLoggedIn = Boolean(this.user());

    this.emailSubscriptionService.subscribe(email, isLoggedIn);
  }
}
